import React from 'react';

// Gradient definitions shared across all figures
const GradientDefs = () => (
  <defs>
    <linearGradient id="bodyGrad" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#4A4A5A" />
      <stop offset="100%" stopColor="#2A2A35" />
    </linearGradient>
    <linearGradient id="muscleActive" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="var(--theme-color)" stopOpacity="0.9" />
      <stop offset="100%" stopColor="var(--theme-color)" stopOpacity="0.4" />
    </linearGradient>
    <radialGradient id="jointGrad">
      <stop offset="0%" stopColor="#6A6A7A" />
      <stop offset="100%" stopColor="#3A3A45" />
    </radialGradient>
    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#8B8B9B" />
      <stop offset="100%" stopColor="#4A4A55" />
    </linearGradient>
  </defs>
);

// Helper: volumetric limb (rounded rect with gradient)
const Limb = ({ x1, y1, x2, y2, w = 6, active, style }) => {
  const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
  const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  return (
    <rect
      x={x1} y={y1 - w / 2}
      width={len} height={w} rx={w / 2}
      fill={active ? 'url(#muscleActive)' : 'url(#bodyGrad)'}
      transform={`rotate(${angle}, ${x1}, ${y1})`}
      style={{ transition: 'all 1s ease-in-out', ...style }}
    />
  );
};

const Joint = ({ cx, cy, r = 4 }) => (
  <circle cx={cx} cy={cy} r={r} fill="url(#jointGrad)" style={{ transition: 'all 1s ease-in-out' }} />
);

const Head = ({ cx, cy, r = 7 }) => (
  <circle cx={cx} cy={cy} r={r} fill="url(#bodyGrad)" stroke="#5A5A6A" strokeWidth="0.5" style={{ transition: 'all 1s ease-in-out' }} />
);

const Bar = ({ x1, y1, x2, y2, w = 3 }) => (
  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#barGrad)" strokeWidth={w} strokeLinecap="round" style={{ transition: 'all 1s ease-in-out' }} />
);

const Plate = ({ cx, cy, r = 5 }) => (
  <circle cx={cx} cy={cy} r={r} fill="#3A3A45" stroke="#5A5A6A" strokeWidth="1" style={{ transition: 'all 1s ease-in-out' }} />
);

// ═══ EXERCISE PATTERNS — Volumetric 3D ═══
const PATTERNS = {
  press_horizontal: {
    viewBox: '0 0 120 80',
    muscles: ['Chest', 'Triceps', 'Front Delt'],
    body: (p) => {
      const armY = p ? 28 : 44;
      return (
        <g>
          <GradientDefs />
          {/* Bench */}
          <rect x="15" y="52" width="65" height="8" rx="3" fill="#1A1A22" stroke="#2A2A35" strokeWidth="0.5" />
          {/* Torso lying */}
          <rect x="28" y="38" width="40" height="12" rx="6" fill={p ? 'url(#muscleActive)' : 'url(#bodyGrad)'} style={{ transition: 'all 1s' }} />
          <Head cx="26" cy="44" r={6} />
          {/* Upper arms */}
          <Limb x1={38} y1={44} x2={38} y2={armY} w={7} active={true} />
          <Limb x1={56} y1={44} x2={56} y2={armY} w={7} active={true} />
          {/* Forearms */}
          <Limb x1={38} y1={armY} x2={38} y2={armY - 2} w={5} active={false} />
          <Limb x1={56} y1={armY} x2={56} y2={armY - 2} w={5} active={false} />
          {/* Bar */}
          <Bar x1={28} y1={armY} x2={66} y2={armY} w={3} />
          <Plate cx={26} cy={armY} r={5} />
          <Plate cx={68} cy={armY} r={5} />
          {/* Joints */}
          <Joint cx={38} cy={44} r={3} />
          <Joint cx={56} cy={44} r={3} />
          {/* Legs */}
          <Limb x1={68} y1={44} x2={80} y2={52} w={7} />
          <Limb x1={80} y1={52} x2={85} y2={44} w={6} />
        </g>
      );
    }
  },
  squat: {
    viewBox: '0 0 80 100',
    muscles: ['Quads', 'Glutes', 'Core'],
    body: (p) => {
      const kneeY = p ? 68 : 52;
      const hipY = p ? 48 : 60;
      const torsoY = p ? 25 : 36;
      return (
        <g>
          <GradientDefs />
          {/* Legs — active */}
          <Limb x1={30} y1={90} x2={30} y2={kneeY} w={8} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={50} y1={90} x2={50} y2={kneeY} w={8} active={true} style={{ transition: 'all 1s' }} />
          {/* Thighs */}
          <Limb x1={30} y1={kneeY} x2={40} y2={hipY} w={9} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={50} y1={kneeY} x2={40} y2={hipY} w={9} active={true} style={{ transition: 'all 1s' }} />
          {/* Torso */}
          <rect x={34} y={torsoY} width={12} height={hipY - torsoY} rx={6} fill="url(#bodyGrad)" style={{ transition: 'all 1s' }} />
          {/* Core highlight */}
          <rect x={36} y={torsoY + 8} width={8} height={12} rx={4} fill="url(#muscleActive)" opacity="0.5" style={{ transition: 'all 1s' }} />
          <Head cx={40} cy={torsoY - 8} />
          {/* Bar on back */}
          <Bar x1={25} y1={torsoY + 2} x2={55} y2={torsoY + 2} w={3} />
          <Plate cx={23} cy={torsoY + 2} r={4} />
          <Plate cx={57} cy={torsoY + 2} r={4} />
          {/* Joints */}
          <Joint cx={30} cy={kneeY} r={3} />
          <Joint cx={50} cy={kneeY} r={3} />
          <Joint cx={40} cy={hipY} r={3} />
        </g>
      );
    }
  },
  deadlift: {
    viewBox: '0 0 80 100',
    muscles: ['Hamstrings', 'Glutes', 'Back'],
    body: (p) => {
      const torsoAngle = p ? 28 : 50;
      const handY = p ? 60 : 78;
      return (
        <g>
          <GradientDefs />
          {/* Legs */}
          <Limb x1={35} y1={90} x2={35} y2={68} w={8} />
          <Limb x1={45} y1={90} x2={45} y2={68} w={8} />
          <Limb x1={35} y1={68} x2={40} y2={58} w={9} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={45} y1={68} x2={40} y2={58} w={9} active={true} style={{ transition: 'all 1s' }} />
          {/* Torso — back active */}
          <rect x={36} y={torsoAngle} width={10} height={58 - torsoAngle} rx={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          <Head cx={40} cy={torsoAngle - 9} />
          {/* Arms hanging */}
          <Limb x1={38} y1={torsoAngle + 10} x2={36} y2={handY} w={5} style={{ transition: 'all 1s' }} />
          <Limb x1={44} y1={torsoAngle + 10} x2={46} y2={handY} w={5} style={{ transition: 'all 1s' }} />
          {/* Bar */}
          <Bar x1={20} y1={handY} x2={62} y2={handY} w={3} />
          <Plate cx={18} cy={handY} r={6} />
          <Plate cx={64} cy={handY} r={6} />
          {/* Joints */}
          <Joint cx={35} cy={68} r={3} />
          <Joint cx={45} cy={68} r={3} />
        </g>
      );
    }
  },
  pullup: {
    viewBox: '0 0 80 110',
    muscles: ['Lats', 'Biceps', 'Rear Delt'],
    body: (p) => {
      const bodyY = p ? 28 : 48;
      return (
        <g>
          <GradientDefs />
          {/* Bar */}
          <Bar x1={15} y1={10} x2={65} y2={10} w={4} />
          {/* Arms — active */}
          <Limb x1={30} y1={10} x2={p ? 36 : 30} y2={p ? 20 : 15} w={6} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={50} y1={10} x2={p ? 44 : 50} y2={p ? 20 : 15} w={6} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={p ? 36 : 30} y1={p ? 20 : 15} x2={40} y2={bodyY} w={6} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={p ? 44 : 50} y1={p ? 20 : 15} x2={40} y2={bodyY} w={6} active={true} style={{ transition: 'all 1s' }} />
          {/* Head */}
          <Head cx={40} cy={bodyY - 1} />
          {/* Torso — lats active */}
          <rect x={34} y={bodyY + 7} width={12} height={24} rx={6} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          {/* Legs hanging */}
          <Limb x1={37} y1={bodyY + 30} x2={35} y2={bodyY + 50} w={7} style={{ transition: 'all 1s' }} />
          <Limb x1={43} y1={bodyY + 30} x2={45} y2={bodyY + 50} w={7} style={{ transition: 'all 1s' }} />
        </g>
      );
    }
  },
  row: {
    viewBox: '0 0 100 90',
    muscles: ['Upper Back', 'Lats', 'Biceps'],
    body: (p) => {
      const handY = p ? 38 : 56;
      return (
        <g>
          <GradientDefs />
          {/* Legs */}
          <Limb x1={28} y1={82} x2={30} y2={62} w={7} />
          <Limb x1={40} y1={82} x2={38} y2={62} w={7} />
          {/* Torso bent — back active */}
          <rect x={32} y={30} width={10} height={32} rx={5} fill="url(#muscleActive)" transform="rotate(20, 37, 46)" style={{ transition: 'all 1s' }} />
          <Head cx={55} cy={24} r={6} />
          {/* Arms */}
          <Limb x1={44} y1={40} x2={48} y2={handY} w={6} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={44} y1={40} x2={40} y2={handY} w={6} active={true} style={{ transition: 'all 1s' }} />
          {/* Bar */}
          <Bar x1={30} y1={handY} x2={58} y2={handY} w={3} />
          <Plate cx={28} cy={handY} r={4} />
          <Plate cx={60} cy={handY} r={4} />
        </g>
      );
    }
  },
  press_overhead: {
    viewBox: '0 0 80 100',
    muscles: ['Shoulders', 'Triceps'],
    body: (p) => {
      const handY = p ? 10 : 32;
      return (
        <g>
          <GradientDefs />
          {/* Legs */}
          <Limb x1={34} y1={92} x2={34} y2={72} w={7} />
          <Limb x1={46} y1={92} x2={46} y2={72} w={7} />
          <Limb x1={34} y1={72} x2={40} y2={60} w={8} />
          <Limb x1={46} y1={72} x2={40} y2={60} w={8} />
          {/* Torso */}
          <rect x={34} y={35} width={12} height={25} rx={6} fill="url(#bodyGrad)" />
          {/* Shoulders — active */}
          <circle cx={34} cy={36} r={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          <circle cx={46} cy={36} r={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          <Head cx={40} cy={26} />
          {/* Arms — active */}
          <Limb x1={34} y1={36} x2={28} y2={handY} w={6} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={46} y1={36} x2={52} y2={handY} w={6} active={true} style={{ transition: 'all 1s' }} />
          {/* Bar */}
          <Bar x1={20} y1={handY} x2={60} y2={handY} w={3} />
          <Plate cx={18} cy={handY} r={4} />
          <Plate cx={62} cy={handY} r={4} />
        </g>
      );
    }
  },
  curl: {
    viewBox: '0 0 60 100',
    muscles: ['Biceps', 'Forearms'],
    body: (p) => {
      const forearmEndY = p ? 40 : 68;
      return (
        <g>
          <GradientDefs />
          {/* Legs */}
          <Limb x1={25} y1={92} x2={25} y2={72} w={7} />
          <Limb x1={35} y1={92} x2={35} y2={72} w={7} />
          <Limb x1={25} y1={72} x2={30} y2={58} w={8} />
          <Limb x1={35} y1={72} x2={30} y2={58} w={8} />
          {/* Torso */}
          <rect x={25} y={30} width={10} height={28} rx={5} fill="url(#bodyGrad)" />
          <Head cx={30} cy={22} />
          {/* Upper arms */}
          <Limb x1={26} y1={34} x2={22} y2={50} w={6} />
          <Limb x1={34} y1={34} x2={38} y2={50} w={6} />
          {/* Forearms — active */}
          <Limb x1={22} y1={50} x2={20} y2={forearmEndY} w={5} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={38} y1={50} x2={40} y2={forearmEndY} w={5} active={true} style={{ transition: 'all 1s' }} />
          {/* Dumbbells */}
          <circle cx={20} cy={forearmEndY} r={4} fill="#3A3A45" stroke="url(#barGrad)" strokeWidth="1.5" style={{ transition: 'all 1s' }} />
          <circle cx={40} cy={forearmEndY} r={4} fill="#3A3A45" stroke="url(#barGrad)" strokeWidth="1.5" style={{ transition: 'all 1s' }} />
          {/* Bicep highlight */}
          <ellipse cx={22} cy={42} rx={4} ry={6} fill="url(#muscleActive)" opacity={p ? 0.8 : 0.3} style={{ transition: 'all 1s' }} />
          <ellipse cx={38} cy={42} rx={4} ry={6} fill="url(#muscleActive)" opacity={p ? 0.8 : 0.3} style={{ transition: 'all 1s' }} />
        </g>
      );
    }
  },
  lateral_raise: {
    viewBox: '0 0 100 90',
    muscles: ['Medial Delt', 'Traps'],
    body: (p) => {
      const armX = p ? 18 : 38;
      const armXR = p ? 82 : 62;
      const armY = p ? 32 : 55;
      return (
        <g>
          <GradientDefs />
          {/* Legs */}
          <Limb x1={44} y1={85} x2={44} y2={65} w={7} />
          <Limb x1={56} y1={85} x2={56} y2={65} w={7} />
          <Limb x1={44} y1={65} x2={50} y2={52} w={8} />
          <Limb x1={56} y1={65} x2={50} y2={52} w={8} />
          {/* Torso */}
          <rect x={44} y={28} width={12} height={24} rx={6} fill="url(#bodyGrad)" />
          {/* Shoulders — active */}
          <circle cx={44} cy={30} r={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          <circle cx={56} cy={30} r={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          <Head cx={50} cy={20} />
          {/* Arms — active */}
          <Limb x1={44} y1={30} x2={armX} y2={armY} w={5} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={56} y1={30} x2={armXR} y2={armY} w={5} active={true} style={{ transition: 'all 1s' }} />
          {/* Dumbbells */}
          <circle cx={armX} cy={armY} r={3.5} fill="#3A3A45" stroke="url(#barGrad)" strokeWidth="1" style={{ transition: 'all 1s' }} />
          <circle cx={armXR} cy={armY} r={3.5} fill="#3A3A45" stroke="url(#barGrad)" strokeWidth="1" style={{ transition: 'all 1s' }} />
        </g>
      );
    }
  },
  lunge: {
    viewBox: '0 0 90 100',
    muscles: ['Quads', 'Glutes', 'Balance'],
    body: (p) => {
      const fKneeY = p ? 62 : 74;
      const hipY = p ? 42 : 54;
      return (
        <g>
          <GradientDefs />
          {/* Front leg — active */}
          <Limb x1={32} y1={92} x2={32} y2={fKneeY} w={8} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={32} y1={fKneeY} x2={45} y2={hipY} w={9} active={true} style={{ transition: 'all 1s' }} />
          {/* Back leg */}
          <Limb x1={62} y1={92} x2={58} y2={p ? 74 : 80} w={7} style={{ transition: 'all 1s' }} />
          <Limb x1={58} y1={p ? 74 : 80} x2={45} y2={hipY} w={8} style={{ transition: 'all 1s' }} />
          {/* Torso */}
          <rect x={40} y={hipY - 22} width={10} height={22} rx={5} fill="url(#bodyGrad)" style={{ transition: 'all 1s' }} />
          <Head cx={45} cy={hipY - 30} />
          {/* Arms */}
          <Limb x1={42} y1={hipY - 16} x2={35} y2={hipY - 10} w={4} style={{ transition: 'all 1s' }} />
          <Limb x1={48} y1={hipY - 16} x2={55} y2={hipY - 10} w={4} style={{ transition: 'all 1s' }} />
          {/* Joints */}
          <Joint cx={32} cy={fKneeY} r={3} />
          <Joint cx={45} cy={hipY} r={3} />
        </g>
      );
    }
  },
  jump: {
    viewBox: '0 0 80 100',
    muscles: ['Quads', 'Calves', 'Explosive'],
    body: (p) => {
      const bodyY = p ? 18 : 52;
      return (
        <g>
          <GradientDefs />
          {/* Box */}
          <rect x={45} y={62} width={28} height={28} rx={3} fill="#1A1A22" stroke="#2A2A35" strokeWidth="0.5" />
          {/* Body */}
          <Head cx={35} cy={bodyY} />
          <rect x={30} y={bodyY + 8} width={10} height={20} rx={5} fill="url(#bodyGrad)" style={{ transition: 'all 0.7s' }} />
          {/* Legs — active */}
          <Limb x1={32} y1={bodyY + 28} x2={28} y2={bodyY + 44} w={8} active={true} style={{ transition: 'all 0.7s' }} />
          <Limb x1={38} y1={bodyY + 28} x2={42} y2={bodyY + 44} w={8} active={true} style={{ transition: 'all 0.7s' }} />
          {/* Arms */}
          <Limb x1={32} y1={bodyY + 12} x2={24} y2={bodyY + 6} w={4} style={{ transition: 'all 0.7s' }} />
          <Limb x1={38} y1={bodyY + 12} x2={46} y2={bodyY + 6} w={4} style={{ transition: 'all 0.7s' }} />
        </g>
      );
    }
  },
  plank: {
    viewBox: '0 0 120 60',
    muscles: ['Core', 'Shoulders', 'Stability'],
    body: (p) => (
      <g>
        <GradientDefs />
        <Head cx={25} cy={26} r={6} />
        {/* Torso — core active */}
        <rect x={30} y={26} width={52} height={10} rx={5} fill="url(#muscleActive)" />
        {/* Arms */}
        <Limb x1={32} y1={30} x2={28} y2={46} w={5} />
        {/* Legs */}
        <Limb x1={82} y1={32} x2={92} y2={46} w={6} />
        <Limb x1={82} y1={32} x2={78} y2={46} w={6} />
        {/* Pulse indicator */}
        <circle cx={55} cy={28} r={3} fill="var(--theme-color)" opacity="0.4">
          <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0.15;0.4" dur="2s" repeatCount="indefinite" />
        </circle>
      </g>
    )
  },
  hip_thrust: {
    viewBox: '0 0 120 70',
    muscles: ['Glutes', 'Hamstrings'],
    body: (p) => {
      const hipY = p ? 20 : 34;
      return (
        <g>
          <GradientDefs />
          {/* Bench */}
          <rect x={8} y={30} width={25} height={32} rx={3} fill="#1A1A22" stroke="#2A2A35" strokeWidth="0.5" />
          <Head cx={26} cy={24} r={5} />
          {/* Back on bench */}
          <Limb x1={30} y1={28} x2={55} y2={hipY} w={8} style={{ transition: 'all 1s' }} />
          {/* Glutes — active */}
          <ellipse cx={55} cy={hipY + 2} rx={8} ry={5} fill="url(#muscleActive)" style={{ transition: 'all 1s' }} />
          {/* Legs */}
          <Limb x1={55} y1={hipY + 4} x2={72} y2={54} w={8} active={true} style={{ transition: 'all 1s' }} />
          <Limb x1={72} y1={54} x2={72} y2={62} w={6} />
          {/* Bar */}
          <Bar x1={48} y1={hipY + 2} x2={62} y2={hipY + 2} w={4} />
        </g>
      );
    }
  },
};

// Map exercise names to patterns (same as before)
const EXERCISE_MAP = {
  'Barbell Bench Press': 'press_horizontal', 'Flat Barbell Bench Press': 'press_horizontal', 'Bench Press': 'press_horizontal',
  'Bench Press (Pause Reps)': 'press_horizontal', 'DB Floor Press': 'press_horizontal', 'Close-Grip Bench Press': 'press_horizontal',
  'Incline Dumbbell Press': 'press_horizontal', 'Incline DB Press': 'press_horizontal', 'Incline Smith Press': 'press_horizontal',
  'Dumbbell Shoulder Press': 'press_overhead', 'Standing Military Press': 'press_overhead', 'Seated DB Shoulder Press': 'press_overhead',
  'Push Press (Barbell)': 'press_overhead', 'Arnold Press': 'press_overhead',
  'Barbell Back Squat': 'squat', 'Back Squat': 'squat', 'Front Squat': 'squat', 'Pause Squat': 'squat', 'Box Squat': 'squat',
  'Trap Bar Deadlift': 'deadlift', 'Romanian Deadlift': 'deadlift', 'Deficit Deadlift': 'deadlift', 'Deadlift': 'deadlift',
  'Single-Leg Romanian DL': 'deadlift',
  'Weighted Pull-Ups': 'pullup', 'Pull-Ups': 'pullup', 'Chin-Ups': 'pullup', 'Weighted Chin-Ups': 'pullup',
  'Lat Pulldown (Wide)': 'pullup', 'Wide-Grip Lat Pulldown': 'pullup', 'Lat Pulldown (Close)': 'pullup',
  'Barbell Bent-Over Row': 'row', 'Barbell Row (Overhand)': 'row', 'Seated Cable Row (Wide)': 'row',
  'Seated Cable Row (V-Grip)': 'row', 'Chest-Supported T-Bar Row': 'row', 'Single-Arm Cable Row': 'row',
  'Single-Arm Dumbbell Row': 'row', 'Supinated Barbell Row': 'row', 'Meadows Row': 'row',
  'EZ-Bar Curl': 'curl', 'Barbell Curl (Straight)': 'curl', 'Incline DB Curl': 'curl', 'Hammer Curls': 'curl',
  'Preacher Curl (EZ Bar)': 'curl', 'Concentration Curls': 'curl', 'Barbell Curl 21s': 'curl',
  'Cable Lateral Raises': 'lateral_raise', 'Dumbbell Flyes (Flat)': 'lateral_raise', 'Cable Face Pulls': 'lateral_raise',
  'Barbell Upright Row': 'lateral_raise', 'Dumbbell Shrugs': 'lateral_raise', 'Reverse Fly (Rear Delt)': 'lateral_raise',
  'Rear Delt Fly (Incline)': 'lateral_raise', 'Face Pulls': 'lateral_raise', 'Band Pull-Aparts': 'lateral_raise',
  'Plank Variations': 'plank', 'Copenhagen Adductor Plank': 'plank', 'Dragon Flags': 'plank',
  'Side-Lying Clam Shells': 'plank', 'Lateral Band Walks': 'plank',
  'Bulgarian Split Squat': 'lunge', 'Dumbbell Walking Lunges': 'lunge', 'Walking Lunges (DB)': 'lunge',
  'Walking Lunge (Barbell)': 'lunge', 'Lateral Step-Up': 'lunge', 'Single-Leg Leg Press': 'lunge',
  'Box Jumps': 'jump', 'Depth Jumps': 'jump', 'Single-Leg Box Jump': 'jump', 'Trap Bar Deadlift Jumps': 'jump',
  'Trap Bar Jump': 'jump', 'Lateral Bounds': 'jump',
  'Hip Thrust (Barbell)': 'hip_thrust', 'Hip Thrust (Banded)': 'hip_thrust', 'Single-Leg Glute Bridge': 'hip_thrust',
  'Glute-Ham Raise': 'hip_thrust',
  'Tricep Rope Pushdowns': 'curl', 'Overhead Tricep Extension': 'press_overhead', 'Skull Crushers': 'press_horizontal',
  'Weighted Dips': 'press_horizontal', 'Diamond Push-Ups': 'press_horizontal', 'Push-Ups (3 variations)': 'press_horizontal',
  'Med Ball Plyo Push-Up': 'press_horizontal',
  'Hanging Leg Raises': 'pullup', 'Hanging Leg Raise': 'pullup', 'Hanging Windshield Wipers': 'pullup',
  'Ab Wheel Rollouts': 'plank', 'Russian Twist (Med Ball)': 'plank',
  'Cable Hip Abduction': 'lateral_raise', 'Nordic Hamstring Curl': 'hip_thrust',
  'Leg Extension': 'lunge', 'Standing Calf Raise': 'lunge', 'Calf Raise Complex': 'lunge',
  'Battle Ropes': 'lateral_raise', 'Med Ball Chest Pass': 'press_horizontal', 'Med Ball Slam': 'deadlift',
  'Farmer\'s Walk': 'deadlift', 'Cable Crossover': 'lateral_raise', 'Cable Woodchop': 'lateral_raise',
  'Landmine Press': 'press_overhead', 'Cable Chest Press': 'press_horizontal', 'Straight Arm Pulldown': 'pullup',
};

// Map exercises to realistic PNG images
const IMAGE_MAP = {
  // Bench / Press Horizontal
  'Barbell Bench Press': '/exercises/bench_press.png',
  'Flat Barbell Bench Press': '/exercises/bench_press.png',
  'Bench Press': '/exercises/bench_press.png',
  'Bench Press (Pause Reps)': '/exercises/bench_press.png',
  'Close-Grip Bench Press': '/exercises/bench_press.png',
  'Incline Dumbbell Press': '/exercises/bench_press.png',
  'Incline DB Press': '/exercises/bench_press.png',
  'Incline Smith Press': '/exercises/bench_press.png',
  'DB Floor Press': '/exercises/bench_press.png',
  'Weighted Dips': '/exercises/bench_press.png',
  'Diamond Push-Ups': '/exercises/bench_press.png',
  'Push-Ups (3 variations)': '/exercises/bench_press.png',
  'Med Ball Plyo Push-Up': '/exercises/bench_press.png',
  'Skull Crushers': '/exercises/bench_press.png',
  'Cable Chest Press': '/exercises/bench_press.png',
  'Med Ball Chest Pass': '/exercises/bench_press.png',
  // Squat
  'Barbell Back Squat': '/exercises/squat.png',
  'Back Squat': '/exercises/squat.png',
  'Front Squat': '/exercises/squat.png',
  'Pause Squat': '/exercises/squat.png',
  'Box Squat': '/exercises/squat.png',
  // Deadlift
  'Trap Bar Deadlift': '/exercises/deadlift.png',
  'Romanian Deadlift': '/exercises/deadlift.png',
  'Deficit Deadlift': '/exercises/deadlift.png',
  'Deadlift': '/exercises/deadlift.png',
  'Single-Leg Romanian DL': '/exercises/deadlift.png',
  'Farmer\'s Walk': '/exercises/deadlift.png',
  'Med Ball Slam': '/exercises/deadlift.png',
  // Pull-up
  'Weighted Pull-Ups': '/exercises/pullup.png',
  'Pull-Ups': '/exercises/pullup.png',
  'Chin-Ups': '/exercises/pullup.png',
  'Weighted Chin-Ups': '/exercises/pullup.png',
  'Lat Pulldown (Wide)': '/exercises/pullup.png',
  'Wide-Grip Lat Pulldown': '/exercises/pullup.png',
  'Lat Pulldown (Close)': '/exercises/pullup.png',
  'Hanging Leg Raises': '/exercises/pullup.png',
  'Hanging Leg Raise': '/exercises/pullup.png',
  'Hanging Windshield Wipers': '/exercises/pullup.png',
  'Straight Arm Pulldown': '/exercises/pullup.png',
  // Shoulder Press
  'Dumbbell Shoulder Press': '/exercises/shoulder_press.png',
  'Standing Military Press': '/exercises/shoulder_press.png',
  'Seated DB Shoulder Press': '/exercises/shoulder_press.png',
  'Push Press (Barbell)': '/exercises/shoulder_press.png',
  'Arnold Press': '/exercises/shoulder_press.png',
  'Overhead Tricep Extension': '/exercises/shoulder_press.png',
  'Landmine Press': '/exercises/shoulder_press.png',
  // Row
  'Barbell Bent-Over Row': '/exercises/row.png',
  'Barbell Row (Overhand)': '/exercises/row.png',
  'Seated Cable Row (Wide)': '/exercises/row.png',
  'Seated Cable Row (V-Grip)': '/exercises/row.png',
  'Chest-Supported T-Bar Row': '/exercises/row.png',
  'Single-Arm Cable Row': '/exercises/row.png',
  'Single-Arm Dumbbell Row': '/exercises/row.png',
  'Supinated Barbell Row': '/exercises/row.png',
  'Meadows Row': '/exercises/row.png',
  // Curl
  'EZ-Bar Curl': '/exercises/curl.png',
  'Barbell Curl (Straight)': '/exercises/curl.png',
  'Incline DB Curl': '/exercises/curl.png',
  'Hammer Curls': '/exercises/curl.png',
  'Preacher Curl (EZ Bar)': '/exercises/curl.png',
  'Concentration Curls': '/exercises/curl.png',
  'Barbell Curl 21s': '/exercises/curl.png',
  'Tricep Rope Pushdowns': '/exercises/curl.png',
  // Lunge
  'Bulgarian Split Squat': '/exercises/lunge.png',
  'Dumbbell Walking Lunges': '/exercises/lunge.png',
  'Walking Lunges (DB)': '/exercises/lunge.png',
  'Walking Lunge (Barbell)': '/exercises/lunge.png',
  'Lateral Step-Up': '/exercises/lunge.png',
  'Single-Leg Leg Press': '/exercises/lunge.png',
  'Leg Extension': '/exercises/lunge.png',
  'Standing Calf Raise': '/exercises/lunge.png',
  'Calf Raise Complex': '/exercises/lunge.png',
  // Lateral Raise
  'Cable Lateral Raises': '/exercises/lateral_raise.png',
  'Dumbbell Flyes (Flat)': '/exercises/lateral_raise.png',
  'Cable Face Pulls': '/exercises/lateral_raise.png',
  'Barbell Upright Row': '/exercises/lateral_raise.png',
  'Dumbbell Shrugs': '/exercises/lateral_raise.png',
  'Reverse Fly (Rear Delt)': '/exercises/lateral_raise.png',
  'Rear Delt Fly (Incline)': '/exercises/lateral_raise.png',
  'Face Pulls': '/exercises/lateral_raise.png',
  'Band Pull-Aparts': '/exercises/lateral_raise.png',
  'Cable Crossover': '/exercises/lateral_raise.png',
  'Cable Woodchop': '/exercises/lateral_raise.png',
  'Cable Hip Abduction': '/exercises/lateral_raise.png',
  'Battle Ropes': '/exercises/lateral_raise.png',
  // Plank / Core
  'Plank Variations': '/exercises/plank.png',
  'Copenhagen Adductor Plank': '/exercises/plank.png',
  'Dragon Flags': '/exercises/plank.png',
  'Ab Wheel Rollouts': '/exercises/plank.png',
  'Russian Twist (Med Ball)': '/exercises/plank.png',
  'Side-Lying Clam Shells': '/exercises/plank.png',
  'Lateral Band Walks': '/exercises/plank.png',
  // Hip Thrust
  'Hip Thrust (Barbell)': '/exercises/hip_thrust.png',
  'Hip Thrust (Banded)': '/exercises/hip_thrust.png',
  'Single-Leg Glute Bridge': '/exercises/hip_thrust.png',
  'Glute-Ham Raise': '/exercises/hip_thrust.png',
  'Nordic Hamstring Curl': '/exercises/hip_thrust.png',
  // Jump (fallback to SVG — no image yet)
  'Box Jumps': null,
  'Depth Jumps': null,
  // Additional exercises — complete coverage
  'Cable Lateral Raise': '/exercises/lateral_raise.png',
  'Cable Lateral + Front Raise SS': '/exercises/lateral_raise.png',
  'DB Press > Lateral Raise SS': '/exercises/shoulder_press.png',
  'Machine Press > Rear Fly SS': '/exercises/bench_press.png',
  'Cable Curl > Pushdown SS': '/exercises/curl.png',
  'Incline DB Fly': '/exercises/bench_press.png',
  'Leg Press': '/exercises/squat.png',
  'Hip Thrust > Band Walk SS': '/exercises/hip_thrust.png',
  'Reverse > Lateral Lunge SS': '/exercises/lunge.png',
  'Walking Lunge > Jump Lunge SS': '/exercises/lunge.png',
  'Box Jump > Broad Jump': null,
  'Single-Leg Box Jump': null,
  'Trap Bar Deadlift Jumps': '/exercises/deadlift.png',
  'Trap Bar Jump': '/exercises/deadlift.png',
  'Lateral Bounds': '/exercises/lunge.png',
  'Single-Leg Continuous Hop': '/exercises/lunge.png',
  "Farmer's Walk": '/exercises/deadlift.png',
  // Recovery / mobility / sport (no specific exercise image — null = SVG fallback)
  'QL Stretch — Right Side': null,
  'Hip Mobility Circuit': null,
  'Foam Roll + Lacrosse Ball': null,
  'Foam Roll Protocol': null,
  'Full Body Foam Roll': null,
  'Yoga / Mobility Flow': null,
  'Walk (Zone 1)': null,
  'Post-Game Recovery': null,
  'Agility Ladder + Cones': null,
  'Court Agility Drills': null,
  'Basketball Skill Work': null,
  'Full Court Basketball': null,
  'Competitive Scrimmage': null,
};

export default function ExerciseAnimation({ exerciseName }) {
  const [phase, setPhase] = React.useState(false);
  const imageUrl = IMAGE_MAP[exerciseName];
  const patternKey = EXERCISE_MAP[exerciseName];
  const pattern = patternKey ? PATTERNS[patternKey] : null;

  React.useEffect(() => {
    const interval = setInterval(() => setPhase(p => !p), 1600);
    return () => clearInterval(interval);
  }, []);

  // Use realistic image if available
  if (imageUrl) {
    return (
      <div className="w-14 h-14 flex-shrink-0 glass-elevated rounded-lg flex items-center justify-center overflow-hidden"
        data-testid="exercise-animation">
        <img src={imageUrl} alt={exerciseName} className="w-13 h-13 object-contain" loading="lazy" />
      </div>
    );
  }

  if (!pattern) return null;

  return (
    <div className="w-14 h-14 flex-shrink-0 glass-elevated rounded-lg flex items-center justify-center overflow-hidden"
      data-testid="exercise-animation">
      <svg viewBox={pattern.viewBox} className="w-12 h-12">
        {pattern.body(phase)}
      </svg>
    </div>
  );
}
